import React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';

interface PremiumEmptyStateProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

const DefaultEmptyIcon = () => (
  <svg className="w-10 h-10 text-brand-teal/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

export const PremiumEmptyState: React.FC<PremiumEmptyStateProps> = ({
  title,
  description,
  icon = <DefaultEmptyIcon />,
  action,
  className
}) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "flex flex-col items-center justify-center p-12 text-center",
        "border border-dashed border-stone-200 dark:border-white/10 rounded-2xl",
        "bg-gradient-to-b from-stone-50/50 to-transparent dark:from-stone-900/30 dark:to-transparent",
        className
      )}
    >
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-brand-plum/20 blur-xl rounded-full" />
        <div className="relative z-10 flex items-center justify-center w-20 h-20 rounded-full bg-stone-100 dark:bg-stone-900 border border-stone-200 dark:border-white/5 shadow-subtle">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-stone-900 dark:text-stone-100 mb-2">
        {title}
      </h3>
      <p className="text-sm text-stone-500 dark:text-stone-400 max-w-sm mb-8 leading-relaxed">
        {description}
      </p>
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </motion.div>
  );
};

export default PremiumEmptyState;
